// Üretim ve lot: yalnız ilgili modül açıkken ve ürün tipinde görünür.
import React from 'react';
import {
  Alert, Box, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select, Switch, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { StockCardForm } from '../useStockCardForm';
import { GroupLabel, SectionTitle } from './fields';

const ITEM_CLASSES = ['RAW', 'SEMI', 'FINISHED', 'PACKAGING', 'BYPRODUCT', 'TRADE'] as const;

export const ProductionLotSection: React.FC<{ f: StockCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');
  const lotJustEnabled = f.trackLots && !(f.itemToEdit as any)?.trackLots && !(f.itemToEdit as any)?.TrackLots;

  return (
    <Box>
      <SectionTitle
        title={t('stock.addModal.layout.sections.production', 'Üretim ve lot')}
        hint={t('stock.addModal.layout.productionHint', 'Ürünün satışta nasıl düşeceği ve lot / son kullanma takibi.')}
      />

      {f.productionEnabled && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="stock-card-item-class-label">{t('stockCard.itemClass', { ns: 'production' })}</InputLabel>
              <Select
                labelId="stock-card-item-class-label"
                id="stock-card-item-class"
                label={t('stockCard.itemClass', { ns: 'production' })}
                value={f.itemClass}
                onChange={(e) => f.setItemClass(String(e.target.value))}
              >
                <MenuItem value="">{t('stockCard.unspecified', { ns: 'production' })}</MenuItem>
                {ITEM_CLASSES.map(c => (
                  <MenuItem key={c} value={c}>{t(`stockCard.classes.${c}`, { ns: 'production' })}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="stock-card-production-mode-label">{t('stockCard.productionMode', { ns: 'production' })}</InputLabel>
              <Select
                labelId="stock-card-production-mode-label"
                id="stock-card-production-mode"
                label={t('stockCard.productionMode', { ns: 'production' })}
                value={f.productionMode}
                onChange={(e) => f.setProductionMode(e.target.value as 'SALE_EXPLODE' | 'MAKE_TO_STOCK')}
              >
                <MenuItem value="SALE_EXPLODE">{t('stockCard.modes.SALE_EXPLODE', { ns: 'production' })}</MenuItem>
                <MenuItem value="MAKE_TO_STOCK">{t('stockCard.modes.MAKE_TO_STOCK', { ns: 'production' })}</MenuItem>
              </Select>
              <FormHelperText>{t(`stockCard.help.${f.productionMode}`, { ns: 'production' })}</FormHelperText>
            </FormControl>
          </Box>
        </>
      )}

      {f.lotTrackingEnabled && (
        <>
          <GroupLabel>{t('stock.addModal.layout.lot', 'Lot ve son kullanma')}</GroupLabel>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={f.trackLots}
                  onChange={(e) => {
                    f.setTrackLots(e.target.checked);
                    if (!e.target.checked) f.setTrackExpiry(false);
                  }}
                />
              )}
              label={t('trackLots', { ns: 'lots' })}
            />
            <FormControlLabel
              control={<Switch checked={f.trackExpiry} disabled={!f.trackLots} onChange={(e) => f.setTrackExpiry(e.target.checked)} />}
              label={t('trackExpiry', { ns: 'lots' })}
            />
          </Box>
          {lotJustEnabled && <Alert severity="info" sx={{ mt: 1 }}>{t('openingNote', { ns: 'lots' })}</Alert>}
          <TextField
            id="stock-card-shelf-life-lot"
            fullWidth
            size="small"
            sx={{ mt: 1.5, maxWidth: { sm: 320 } }}
            label={t('stock.addModal.layout.shelfLife', 'Raf ömrü (gün)')}
            value={f.shelfLifeDays}
            onChange={(e) => f.setShelfLifeDays(e.target.value.replace(/\D/g, '').slice(0, 4))}
            helperText={t('shelfHelp', { ns: 'lots' })}
            inputProps={{ inputMode: 'numeric', maxLength: 4 }}
          />
        </>
      )}
    </Box>
  );
};
